import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Download, Search, Lock, Unlock, Eye,
  FileSignature, ClipboardList, Palette, Scale, Receipt,
  BookOpen, CheckSquare, Calculator, ShieldCheck, Home,
  Scroll, Folder, Loader2, Grid, List, Star, ArrowRight,
  Presentation, Crown, AlertCircle, MessageSquarePlus, ThumbsUp,
  Send, X, ChevronUp, Clock, CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_CATEGORIES = {
  contratos: { name: 'Contratos', icon: FileSignature, color: 'bg-blue-500', lightColor: 'bg-blue-100 text-blue-700' },
  formatos: { name: 'Formatos de Trabajo', icon: ClipboardList, color: 'bg-green-500', lightColor: 'bg-green-100 text-green-700' },
  templates_mkt: { name: 'Templates Marketing', icon: Palette, color: 'bg-pink-500', lightColor: 'bg-pink-100 text-pink-700' },
  legal: { name: 'Documentos Legales', icon: Scale, color: 'bg-purple-500', lightColor: 'bg-purple-100 text-purple-700' },
  fiscal: { name: 'Documentos Fiscales', icon: Receipt, color: 'bg-yellow-500', lightColor: 'bg-yellow-100 text-yellow-700' },
  guias: { name: 'Guías y Manuales', icon: BookOpen, color: 'bg-indigo-500', lightColor: 'bg-indigo-100 text-indigo-700' },
  checklists: { name: 'Checklists', icon: CheckSquare, color: 'bg-teal-500', lightColor: 'bg-teal-100 text-teal-700' },
  presentaciones: { name: 'Presentaciones', icon: Presentation, color: 'bg-orange-500', lightColor: 'bg-orange-100 text-orange-700' },
  calculadoras: { name: 'Calculadoras Excel', icon: Calculator, color: 'bg-emerald-500', lightColor: 'bg-emerald-100 text-emerald-700' },
  nom247: { name: 'NOM-247', icon: ShieldCheck, color: 'bg-red-500', lightColor: 'bg-red-100 text-red-700' },
  avaluos: { name: 'Avalúos', icon: Home, color: 'bg-cyan-500', lightColor: 'bg-cyan-100 text-cyan-700' },
  escrituras: { name: 'Escrituras', icon: Scroll, color: 'bg-amber-500', lightColor: 'bg-amber-100 text-amber-700' },
  otros: { name: 'Otros', icon: Folder, color: 'bg-gray-500', lightColor: 'bg-gray-100 text-gray-700' }
};

const STATUS_INFO = {
  pending: { name: 'Pendiente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  in_progress: { name: 'En Proceso', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completed: { name: 'Completado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { name: 'Rechazado', color: 'bg-red-100 text-red-700', icon: X }
};

export default function BibliotecaPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [accessInfo, setAccessInfo] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  
  // Request feature states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRequestsPanel, setShowRequestsPanel] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ title: '', description: '', resource_category: 'otros' });
  const [requestSuccess, setRequestSuccess] = useState('');

  useEffect(() => {
    loadMaterials();
    loadRequests();
  }, [filterCategory]);

  const loadRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await axios.get(`${API_URL}/api/content/resource-requests?limit=20`);
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      setSubmittingRequest(true);
      await axios.post(`${API_URL}/api/content/resource-requests`, requestForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestSuccess('¡Solicitud enviada! La revisaremos pronto.');
      setRequestForm({ title: '', description: '', resource_category: 'otros' });
      setShowRequestModal(false);
      loadRequests();
      setTimeout(() => setRequestSuccess(''), 5000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleUpvote = async (requestId) => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/content/resource-requests/${requestId}/upvote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadRequests();
    } catch (error) {
      if (error.response?.status === 400) {
        alert('Ya votaste por esta solicitud');
      }
    }
  };

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCategory) params.append('resource_category', filterCategory);
      params.append('limit', '100');

      const response = await axios.get(`${API_URL}/api/content/biblioteca/public?${params.toString()}`);
      setMaterials(response.data.materials || []);
      setCategoryCounts(response.data.category_counts || {});
    } catch (error) {
      console.error('Error loading materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClick = async (material) => {
    setSelectedMaterial(material);
    setCheckingAccess(true);
    setShowAccessModal(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API_URL}/api/content/biblioteca/check-access/${material.id}`,
        { headers }
      );
      setAccessInfo(response.data);

      if (response.data.can_download) {
        // Can download - get the link
        const downloadRes = await axios.get(
          `${API_URL}/api/content/materials/${material.id}/download`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.open(`${API_URL}${downloadRes.data.download_url}`, '_blank');
        setShowAccessModal(false);
      }
    } catch (error) {
      setAccessInfo({
        can_download: false,
        reason: error.response?.data?.detail || 'Error al verificar acceso',
        upgrade_url: '/planes'
      });
    } finally {
      setCheckingAccess(false);
    }
  };

  const filteredMaterials = materials.filter(m =>
    m.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const featuredMaterials = filteredMaterials.filter(m => m.is_featured);
  const regularMaterials = filteredMaterials.filter(m => !m.is_featured);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-700 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Biblioteca de Recursos Profesionales
          </h1>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl">
            Contratos, formatos, templates y documentos esenciales para tu éxito en el sector inmobiliario
          </p>
          
          {/* Search */}
          <div className="max-w-xl">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar contratos, formatos, templates..."
                className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-4 focus:ring-white/30"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2">
          <button
            onClick={() => setFilterCategory('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              !filterCategory 
                ? 'bg-purple-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-purple-50 border border-gray-200'
            }`}
          >
            Todos ({materials.length})
          </button>
          {Object.entries(RESOURCE_CATEGORIES).map(([key, cat]) => {
            const count = categoryCounts[key] || 0;
            if (count === 0) return null;
            const IconComponent = cat.icon;
            return (
              <button
                key={key}
                onClick={() => setFilterCategory(filterCategory === key ? '' : key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  filterCategory === key 
                    ? `${cat.color} text-white` 
                    : 'bg-white text-gray-700 hover:bg-purple-50 border border-gray-200'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                {cat.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Membership CTA for non-members */}
        {!user && (
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 mb-8 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Crown className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Accede a todos los recursos</h3>
                  <p className="text-purple-100">Hazte socio de UPIM y descarga contratos, formatos y más</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/planes')}
                className="px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-purple-50 flex items-center gap-2"
              >
                Ver planes
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {requestSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {requestSuccess}
          </div>
        )}

        {/* Request Resource CTA */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <MessageSquarePlus className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">¿No encuentras lo que buscas?</h3>
                <p className="text-gray-600">Solicita un documento y lo subiremos para ti y toda la comunidad</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRequestsPanel(!showRequestsPanel)}
                className="px-4 py-3 border border-amber-300 text-amber-700 rounded-xl font-semibold hover:bg-amber-100 flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Ver solicitudes ({requests.length})
              </button>
              <button
                onClick={() => user ? setShowRequestModal(true) : navigate('/login')}
                className="px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Solicitar recurso
              </button>
            </div>
          </div>
        </div>

        {/* Requests Panel */}
        {showRequestsPanel && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-amber-500" />
                Solicitudes de la Comunidad
              </h3>
              <button onClick={() => setShowRequestsPanel(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {loadingRequests ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" />
              </div>
            ) : requests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay solicitudes aún. ¡Sé el primero!</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {requests.map(req => {
                  const category = RESOURCE_CATEGORIES[req.resource_category] || RESOURCE_CATEGORIES.otros;
                  const status = STATUS_INFO[req.status] || STATUS_INFO.pending;
                  const StatusIcon = status.icon;
                  
                  return (
                    <div key={req.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-gray-900">{req.title}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${status.color} flex items-center gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-2">{req.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span className={`px-2 py-0.5 rounded ${category.lightColor}`}>
                              {category.name}
                            </span>
                            <span>Por {req.user_name || 'Usuario'}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUpvote(req.id)}
                          className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-amber-50 text-gray-500 hover:text-amber-600"
                          title="Votar por esta solicitud"
                        >
                          <ChevronUp className="w-5 h-5" />
                          <span className="text-sm font-bold">{req.upvotes || 1}</span>
                        </button>
                      </div>
                      {req.admin_response && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
                          <p className="font-medium text-blue-700">Respuesta del equipo:</p>
                          <p className="text-blue-600">{req.admin_response}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <p className="text-3xl font-bold text-gray-900">{materials.length}</p>
            <p className="text-sm text-gray-500">Recursos disponibles</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <p className="text-3xl font-bold text-gray-900">{Object.keys(categoryCounts).length}</p>
            <p className="text-sm text-gray-500">Categorías</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <p className="text-3xl font-bold text-gray-900">{materials.filter(m => m.is_featured).length}</p>
            <p className="text-sm text-gray-500">Destacados</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <p className="text-3xl font-bold text-gray-900">
              {materials.reduce((acc, m) => acc + (m.download_count || 0), 0)}
            </p>
            <p className="text-sm text-gray-500">Descargas totales</p>
          </div>
        </div>

        {/* Featured Materials */}
        {featuredMaterials.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Recursos Destacados
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredMaterials.map(material => (
                <MaterialCard 
                  key={material.id} 
                  material={material} 
                  onDownload={handleDownloadClick}
                  featured
                />
              ))}
            </div>
          </div>
        )}

        {/* All Materials */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500 mb-2" />
            <p className="text-gray-500">Cargando recursos...</p>
          </div>
        ) : regularMaterials.length === 0 && featuredMaterials.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No hay recursos en esta categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {regularMaterials.map(material => (
              <MaterialCard 
                key={material.id} 
                material={material} 
                onDownload={handleDownloadClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Access Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            {checkingAccess ? (
              <div className="text-center py-8">
                <Loader2 className="w-10 h-10 animate-spin mx-auto text-purple-500 mb-4" />
                <p className="text-gray-600">Verificando acceso...</p>
              </div>
            ) : accessInfo && !accessInfo.can_download ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Contenido Exclusivo para Socios
                </h3>
                <p className="text-gray-600 mb-2">
                  <strong>{selectedMaterial?.title}</strong>
                </p>
                <p className="text-gray-500 mb-6">{accessInfo.reason}</p>
                
                <div className="space-y-3">
                  {!user ? (
                    <>
                      <button
                        onClick={() => { setShowAccessModal(false); navigate('/login'); }}
                        className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700"
                      >
                        Iniciar Sesión
                      </button>
                      <button
                        onClick={() => { setShowAccessModal(false); navigate('/planes'); }}
                        className="w-full py-3 border border-purple-200 text-purple-600 rounded-xl font-semibold hover:bg-purple-50"
                      >
                        Conocer Planes de Membresía
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { setShowAccessModal(false); navigate('/planes'); }}
                        className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 flex items-center justify-center gap-2"
                      >
                        <Crown className="w-5 h-5" />
                        Actualizar Membresía
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowAccessModal(false)}
                    className="w-full py-3 text-gray-500 hover:text-gray-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Request Resource Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-amber-500" />
                Solicitar Recurso
              </h3>
              <button onClick={() => setShowRequestModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">¿Qué documento necesitas? *</label>
                <input
                  type="text"
                  value={requestForm.title}
                  onChange={(e) => setRequestForm({ ...requestForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: Contrato de exclusividad para terrenos"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Describe lo que necesitas *</label>
                <textarea
                  value={requestForm.description}
                  onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  rows={3}
                  placeholder="Describe para qué lo usarías, qué cláusulas debe tener, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <select
                  value={requestForm.resource_category}
                  onChange={(e) => setRequestForm({ ...requestForm, resource_category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {Object.entries(RESOURCE_CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submittingRequest}
                  className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 flex items-center justify-center gap-2"
                >
                  {submittingRequest ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Solicitud
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MaterialCard({ material, onDownload, featured = false }) {
  const category = RESOURCE_CATEGORIES[material.resource_category] || RESOURCE_CATEGORIES.otros;
  const IconComponent = category.icon;
  const requiresPayment = material.requires_membership;

  return (
    <div className={`bg-white rounded-xl border ${featured ? 'border-yellow-200 ring-2 ring-yellow-100' : 'border-gray-100'} p-4 hover:shadow-lg transition-all group`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${category.lightColor}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <div className="flex items-center gap-1">
          {featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
          {requiresPayment ? (
            <Lock className="w-4 h-4 text-purple-500" />
          ) : (
            <Unlock className="w-4 h-4 text-green-500" />
          )}
        </div>
      </div>
      
      <h3 className="font-medium text-gray-900 line-clamp-2 mb-1" title={material.title}>
        {material.title}
      </h3>
      
      {material.description && (
        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{material.description}</p>
      )}
      
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded ${category.lightColor}`}>
          {category.name}
        </span>
        {requiresPayment && (
          <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
            Solo Socios
          </span>
        )}
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {material.file_size_mb} MB · {material.download_count || 0} descargas
        </span>
        <button
          onClick={() => onDownload(material)}
          className={`p-2 rounded-lg transition-colors ${
            requiresPayment 
              ? 'bg-purple-100 text-purple-600 hover:bg-purple-200' 
              : 'bg-green-100 text-green-600 hover:bg-green-200'
          }`}
          title={requiresPayment ? 'Requiere membresía' : 'Descargar'}
        >
          {requiresPayment ? <Lock className="w-4 h-4" /> : <Download className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
